import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/features/auth/use-auth';
import { ProvisioningSteps } from '@/features/provisioning/provisioning-steps';
import { useBasicProvisioningMutation } from '@/features/provisioning/provisioning.mutation';
import {
  JurisdictionTranslations,
  OrganizationCustomAttributesSchema,
  Provisioning,
  SectorTranslations,
  ServiceTier,
  ServiceTierSchema,
} from '@/features/provisioning/provisioning.type';
import { AsyncButton } from '@/shared/components/button/async-button';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const formSchema = z.object({
  customAttributes: OrganizationCustomAttributesSchema,
  organisationName: z.string().min(1, {
    message: 'Organisationsname ist erforderlich',
  }),
  serviceTier: ServiceTierSchema,
});
export type ProvisioningBasicFormValues = z.infer<typeof formSchema>;

export function ProvisioningBasicForm({
  context,
  provisioning,
}: {
  context: ServiceTier;
  provisioning: Provisioning | undefined;
}) {
  const form = useForm<ProvisioningBasicFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      customAttributes: {
        jurisdiction: 'EU',
        sector: 'PUBLIC_SECTOR',
      },
      organisationName: provisioning?.organisationName ?? '',
      serviceTier: 'BASIC',
    },
  });

  const { refreshTokens } = useAuth();
  const basicProvisioningMutation = useBasicProvisioningMutation();

  const onSubmit = async (values: ProvisioningBasicFormValues) => {
    try {
      await basicProvisioningMutation.mutateAsync(values);
      await refreshTokens();
      form.reset();
    } catch (error) {
      console.error(error);
    }
  };

  const title =
    context === 'BASIC'
      ? 'Willkommen zur embraceableAI API Konsole'
      : 'Upgrade auf embraceableAI Pro';

  const description =
    context === 'BASIC'
      ? 'Bevor Sie kostenlos den API Zugriff testen können, benötigen wir zusätzliche Informationen zu Ihrer Organisation.'
      : 'Nutze die volle Funktionalität der embraceableAI Plattform.';

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">{title}</h1>
        <p className="text-muted-foreground">{description}</p>
      </div>

      {context === 'PRO' && <ProvisioningSteps activeStep={0} />}

      {provisioning?.status === 'CREATE_FAILED' && (
        <div className="bg-warning rounded-lg p-4">
          <h2 className="">
            Beim Erstellen der Organisation ist ein Fehler
            aufgetreten. Bitte versuchen Sie es erneut.
          </h2>
        </div>
      )}

      <Form {...form}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <FormField
            control={form.control}
            name="organisationName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organisationsname</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Max Mustermann GmbH"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="customAttributes.sector"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Branche</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  name={field.name}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Wählen Sie Ihre Branche" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.entries(SectorTranslations).map(
                      ([key, value]) => (
                        <SelectItem
                          key={key}
                          value={key.toString()}
                        >
                          {value}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="customAttributes.jurisdiction"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rechtsgebiet</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  name={field.name}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Wählen Sie Ihr Rechtsgebiet" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.entries(JurisdictionTranslations).map(
                      ([key, value]) => (
                        <SelectItem
                          key={key}
                          value={key.toString()}
                        >
                          {value}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <AsyncButton
            isLoading={basicProvisioningMutation.isPending}
            type="submit"
            className="mt-2 w-full"
          >
            Kostenlos den API Zugriff testen
          </AsyncButton>
        </form>
      </Form>
    </div>
  );
}
